import type React from "react"
import { useTranslation } from "react-i18next"

import { MoreMenu } from "@/nzds/inputs/more-menu"
import { HStack } from "@/nzds/layout"

import { Badge, Box, IconButton } from "@mui/material"
import {
  ColumnsPanelTrigger,
  FilterPanelTrigger,
  Toolbar,
  useGridRootProps,
} from "@mui/x-data-grid-pro"

import { DataGridRecordCount } from "./data-grid-record-count"
import type { DataGridToolbarProps } from "./data-grid-toolbar.types"

/**
 * Custom toolbar for the DataGrid component. Must be used in the toolbar slot.
 */
export const DataGridToolbar: React.FC<DataGridToolbarProps> = ({
  endActions,
  isMoreMenuDisabled = false,
  isMoreMenuLoading = false,
  isRecordCountLoading,
  moreMenuItems,
  recordCount,
  showColumnSelector = false,
  showFilters = false,
  startActions,
}) => {
  const { t } = useTranslation()
  const gridRootProps = useGridRootProps()
  const ColumnSelectorIcon = gridRootProps.slots.columnSelectorIcon
  const FilterIcon = gridRootProps.slots.openFilterButtonIcon
  const showColumns = showColumnSelector && !gridRootProps.disableColumnSelector
  const showFilterButton = showFilters && !gridRootProps.disableColumnFilter
  const showRecordCount =
    isRecordCountLoading !== undefined || recordCount !== undefined

  return (
    <Toolbar style={showRecordCount ? { alignItems: "center" } : undefined}>
      <Box sx={{ p: 0.5, width: "100%" }}>
        <HStack
          sx={{
            alignItems: "center",
            flexGrow: 1,
            justifyContent: "space-between",
          }}
        >
          <HStack
            spacing={2}
            sx={showRecordCount ? { alignItems: "center" } : undefined}
          >
            {showRecordCount ? (
              <DataGridRecordCount
                count={recordCount}
                isLoading={isRecordCountLoading ?? false}
              />
            ) : null}
            {startActions ?? null}
            {showColumns ? (
              <ColumnsPanelTrigger
                aria-label={gridRootProps.localeText.toolbarColumnsLabel}
                // @ts-expect-error MUI X baseButton typing misses runtime-supported color prop
                color="neutral"
                size="small"
                startIcon={<ColumnSelectorIcon fontSize="small" />}
                variant="outlined"
              >
                {gridRootProps.localeText.toolbarColumns}
              </ColumnsPanelTrigger>
            ) : null}
            {showFilterButton ? (
              <FilterPanelTrigger
                render={(props, state) => {
                  const Tooltip = gridRootProps.slots.baseTooltip

                  return (
                    <Tooltip title={gridRootProps.localeText.toolbarFilters}>
                      <IconButton
                        {...props}
                        aria-label={
                          gridRootProps.localeText.toolbarFiltersLabel
                        }
                        color="neutral"
                        size="small"
                      >
                        <Badge
                          badgeContent={state.filterCount}
                          color="primary"
                          sx={{
                            "& .MuiBadge-badge": {
                              // Default placement hangs off the button corner.
                              // Inset keeps the dot on the icon.
                              inset: "3px 2px auto auto",
                            },
                          }}
                          variant="dot"
                        >
                          <FilterIcon fontSize="small" />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                  )
                }}
              />
            ) : null}
            {endActions ?? null}
          </HStack>
          {moreMenuItems ? (
            <MoreMenu
              ariaLabel={t("button.more", { ns: "common" })}
              disabled={isMoreMenuDisabled}
              loading={isMoreMenuLoading}
              moreMenuItems={moreMenuItems}
            />
          ) : null}
        </HStack>
      </Box>
    </Toolbar>
  )
}
